import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import helpButtonIcon from 'assets/helpButton.svg';

import { ToolTip } from 'Components/Tooltip';

import { CategoryFilter } from 'Pages/TeamDashboard/CreateReview/components/CategoryFilter';
import { DisclaimerFooter } from 'Pages/TeamDashboard/CreateReview/components/DisclaimerFooter';
import { FeedbackBox } from 'Pages/TeamDashboard/CreateReview/components/FeedbackBox';

import { FEEDBACK_EDGE_CASES } from 'utlis/constants';

import styles from './styles.module.scss';

export const FeedbackList = ({
  feedback,
  categoryName,
  watchDemo,
  isPublicView = false,
  onCategoryClick,
  onInspectFeedback,
  totalImages = 0,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const [showPopUp, setShowPopUp] = useState(false);

  const handleCategorySelect = categoryKey => {
    if (onCategoryClick) {
      // If parent component handles category navigation, use that
      onCategoryClick(categoryKey);
    }
  };

  useEffect(() => {
    if (showPopUp) {
      const timer = setTimeout(() => {
        setShowPopUp(false);
      }, 5000); // Hide after 5 seconds

      // Cleanup the timer on component unmount or when showPopUp changes
      return () => clearTimeout(timer);
    }
  }, [showPopUp]);

  const feedbackIndexMap = useMemo(() => {
    const map = new Map();

    currentRecord.final_feedback.forEach((feedbackItem, index) => {
      map.set(feedbackItem, index);
    });

    return map;
  }, [currentRecord]);

  const errorCardValues = useMemo(() => {
    if (!feedback.length) {
      return FEEDBACK_EDGE_CASES.NO_FEEDBACK;
    }
    if (currentRecord?.extra_data?.page_type === null) {
      return FEEDBACK_EDGE_CASES.NOT_SUPPORTED;
    }

    return null;
  }, [currentRecord]);

  // Count dismissed feedbacks for public view
  const dismissedCount = useMemo(() => {
    if (!isPublicView) {
      return 0;
    }

    return feedback.filter(val => val.dismiss === true).length;
  }, [feedback, isPublicView]);

  return (
    <div
      className={cn(
        'max-w-md 2xl:max-w-[470px] mx-8',
        styles['feedback-page'],
        styles['feedback-container-with-footer']
      )}
    >
      {!errorCardValues
        ? (
          <>
            {/* Category Filter */}
            <CategoryFilter
              categoryName={categoryName}
              onCategorySelect={handleCategorySelect}
            />
            <div className={cn(styles['feedback-container'])}>
              {feedback.map((val, ind) => (
                <FeedbackBox
                  data={val}
                  key={`${categoryName}-${ind}-${val.issue || val.detailed_feedback?.[0] || ind}`}
                  index={ind}
                  currentFeedback={val.index !== undefined ? val.index + 1 : feedbackIndexMap.get(val) + 1}
                  selectedCategory={categoryName}
                  selectedLabelCategory="All themes"
                  isPublicView={isPublicView}
                  onInspectFeedback={onInspectFeedback}
                  totalImages={totalImages}
                />
              ))}
              {isPublicView && dismissedCount > 0 && (
                <div className="flex items-center gap-8 py-2 mb-24">
                  <span className="font-normal text-sm leading-5 text-left" style={{ color: '#8F97A5' }}>
                    {dismissedCount}
                    {' '}
                    {dismissedCount === 1 ? 'finding has' : 'findings have'}
                    {' '}
                    been hidden
                  </span>
                  <ToolTip
                    title="Reviewers can hide findings based on relevance or accuracy"
                    position="right"
                    width="150px"
                  >
                    <img
                      src={helpButtonIcon}
                      alt="Help"
                      className="cursor-pointer"
                      style={{ width: '16px', height: '16px' }}
                    />
                  </ToolTip>
                </div>
              )}
            </div>
          </>
        )
        : (
          <div className={styles['error-container']}>
            <img className={styles['image']} src={errorCardValues.image} alt="a problem occurred" />
            <div className={styles['text-container']}>
              <h2 className={styles['title']}>
                {errorCardValues.title}
              </h2>
              <p className={styles['sub-title']}>
                {errorCardValues.description}
              </p>
              <div className={styles['bottom-container']}>
                {errorCardValues === FEEDBACK_EDGE_CASES.NOT_SUPPORTED && (
                  <button className={styles['button']} onClick={watchDemo}>
                    Watch demo
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Disclaimer Footer */}
      <DisclaimerFooter />
    </div>
  );
};

FeedbackList.propTypes = {
  feedback: PropTypes.array.isRequired,
  categoryName: PropTypes.string.isRequired,
  watchDemo: PropTypes.func.isRequired,
  isPublicView: PropTypes.bool,
  onCategoryClick: PropTypes.func,
  onInspectFeedback: PropTypes.func,
  totalImages: PropTypes.number,
};
